import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Download,
  Edit2,
  FileImage,
  FilePenLine,
  Loader,
  MoreVertical,
  Send,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SignatureField } from './SignatureFieldOverlay';
import { SendForReviewDialog } from './SendForReviewDialog';
import DocumentStatusBadge from '@/modules/org/contracts/components/document-status-badge';
import type { DocumentStatus } from '@/modules/org/contracts/constants/mock-contract-documents';

export type SaveDraftStatus = 'idle' | 'saving' | 'saved' | 'error';

interface TemplatePDFReviewHeaderProps {
  templateTitle: string;
  templateSlug?: string;
  documentId?: string | null;
  numPages: number | null;
  signatureFields: SignatureField[];
  signatoriesLength: number;
  hasUnsavedChanges: boolean;
  isAuthenticated: boolean;
  /** Navigate back to the generate step. Renders the back arrow when provided. */
  onBack?: () => void;
  /** Edit the document title. When provided (and editable), the title is clickable. */
  onEditTitle?: () => void;
  /**
   * Click handler for the Letterhead button. The parent decides what to do
   * based on auth/payment state.
   */
  onLetterheadClick: () => void;
  /**
   * Whether this document model supports letterheads at all. False for
   * fixed-DOCX contracts, which carry their own headers/footers from the Word
   * template. Distinct from `isEditable`, which is about document state.
   */
  canLetterhead?: boolean;
  setShowDownloadPrompt: (show: boolean) => void;
  handleSendToSignature: () => void;
  /** Click handler for the Save-as-Draft button. */
  onSaveDraft: () => void;
  /** Current save state — drives the button's spinner / check / error UI. */
  saveDraftStatus: SaveDraftStatus;
  /** Hide the button entirely (e.g. when no draftId is present). */
  canSaveDraft: boolean;
  /**
   * Whether the document is in an editable state (`draft` or
   * `generation_completed`). When false, Send-for-Signature and Letterhead
   * buttons are hidden and the document's current Status is shown as a badge.
   */
  isEditable: boolean;
  /** Current Status from OrgContractDocument; used for the badge when not editable. */
  documentStatus?: DocumentStatus;
  handleDownloadPdf: () => void;
  /**
   * A download is in flight. Every review surface builds its PDF asynchronously
   * — converting a .docx server-side, or fetching a stored one — so the button
   * has to say so, otherwise the click looks like it did nothing and gets
   * repeated.
   */
  isDownloading?: boolean;
  hideDocumentActions?: boolean;
  isApprovalPage?: boolean;
  isContractApproved?: boolean;
  hasSentForApproval?: boolean;
}

const TemplatePDFReviewHeader = ({
  templateTitle,
  templateSlug,
  documentId,
  numPages,
  signatureFields,
  signatoriesLength,
  hasUnsavedChanges,
  isAuthenticated,
  onBack,
  onEditTitle,
  onLetterheadClick,
  canLetterhead = true,
  setShowDownloadPrompt,
  handleSendToSignature,
  onSaveDraft,
  saveDraftStatus,
  canSaveDraft,
  isEditable,
  documentStatus,
  handleDownloadPdf,
  isDownloading = false,
  hideDocumentActions = false,
  isApprovalPage = false,
  isContractApproved = false,
  hasSentForApproval = false,
}: TemplatePDFReviewHeaderProps) => {
  const { t } = useTranslation();
  const [isSendToSignatureModalOpen, setIsSendToSignatureModalOpen] = useState(false);
  const isPendingApproval =
    !isApprovalPage && hasSentForApproval && !isContractApproved;

  const downloadLabel = isDownloading
    ? t('GENERATEREVIEW_ACTIONS_PREPARINGPDF', { defaultValue: 'Preparing…' })
    : t('GENERATEREVIEW_ACTIONS_DOWNLOADPDF');

  const handleDownloadButtonClick = () => {
    if (isDownloading) return;
    if (!isEditable) {
      handleDownloadPdf();
    } else {
      setShowDownloadPrompt(true);
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-[hsl(var(--bg))]/85 backdrop-blur-xl">
      <div className="px-4 md:px-5 py-2">
        <div className="flex items-center justify-between gap-3 md:gap-4">
          {/* Left cluster — back arrow, title, single-line status/meta row. */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {onBack && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                aria-label={t('GENERATEREVIEW_ACTIONS_BACK')}
                className="shrink-0 -ml-1.5 text-globe-grey hover:text-[hsl(var(--fg))]"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}

            <div className="min-w-0">
              {onEditTitle && isEditable ? (
                <button
                  type="button"
                  onClick={onEditTitle}
                  title={t('GENERATEREVIEW_VBLOCKS_TITLE', { defaultValue: 'Document Title' })}
                  className="group/title flex items-center gap-1.5 min-w-0 text-left rounded -mx-1 px-1 hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue"
                >
                  <h1 className="text-base md:text-lg font-semibold leading-tight truncate text-[hsl(var(--fg))]">
                    {templateTitle}
                  </h1>
                  <Edit2 className="w-4 h-4 shrink-0 text-globe-grey group-hover/title:text-selise-blue transition-colors" />
                </button>
              ) : (
                <h1 className="text-base md:text-lg font-semibold leading-tight truncate text-[hsl(var(--fg))]">
                  {templateTitle}
                </h1>
              )}

              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs sm:text-sm text-globe-grey">
                {/* Status: locked → status badge; edited → amber; otherwise → ready */}
                {!isEditable && documentStatus ? (
                  <DocumentStatusBadge status={documentStatus} />
                ) : !isApprovalPage && hasSentForApproval && isContractApproved ? (
                  <DocumentStatusBadge
                    status="document_completed"
                    label={t('GENERATEREVIEW_STATUS_APPROVED', { defaultValue: 'Approved' })}
                  />
                ) : !isApprovalPage && hasSentForApproval ? (
                  <DocumentStatusBadge
                    status="rollout_success"
                    label={t('GENERATEREVIEW_STATUS_PENDING_APPROVAL', {
                      defaultValue: 'Pending Approval',
                    })}
                  />
                ) : hasUnsavedChanges ? (
                  <span className="inline-flex items-center gap-1 font-medium text-[hsl(var(--warning))]">
                    <Edit2 className="w-3.5 h-3.5" />
                    {t('GENERATEREVIEW_STATUS_EDITED')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-medium text-poly-green">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t('GENERATEREVIEW_STATUS_READY')}
                  </span>
                )}

                {signatoriesLength > 0 && (
                  <span>
                    {t('GENERATEREVIEW_STATUS_SIGNATORIESCOUNT', { count: signatoriesLength })}
                  </span>
                )}

                {numPages && <span>{t('GENERATEREVIEW_META_PAGECOUNT', { count: numPages })}</span>}
              </div>
            </div>
          </div>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {isApprovalPage && isContractApproved && (
              <DocumentStatusBadge
                status="document_completed"
                label={t('GENERATEREVIEW_STATUS_APPROVED', { defaultValue: 'Approved' })}
              />
            )}
            {isEditable && signatureFields.length > 0 && (
              <Button
                onClick={() => {
                  if (isApprovalPage || isContractApproved) {
                    handleSendToSignature();
                  } else {
                    setIsSendToSignatureModalOpen(true);
                  }
                }}
                variant="primary"
                size={'sm'}
                disabled={isPendingApproval}
                title={
                  isPendingApproval
                    ? t('GENERATEREVIEW_PENDING_APPROVAL_SIGNATURE_DISABLED', {
                      defaultValue: 'The contract must be approved before it can be sent for signature.',
                    })
                    : undefined
                }
              >
                {isApprovalPage && !isContractApproved ? (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {isApprovalPage && !isContractApproved
                  ? t('GENERATEREVIEW_ACTIONS_APPROVECONTRACT', {
                    defaultValue: 'Approve Contract',
                  })
                  : t('GENERATEREVIEW_ACTIONS_SENDFORSIGNATURE')}
              </Button>
            )}
            {/* Letterhead is editable-only — the document is locked once the
                signature workflow has progressed past generation_completed —
                and unsupported entirely by document models that bring their own
                headers/footers. */}
            {isEditable && canLetterhead && !hideDocumentActions && (
              <Button
                variant="outline"
                size={'sm'}
                onClick={onLetterheadClick}
                title={t('GENERATEREVIEW_ACTIONS_UPLOADLETTERHEAD')}
              >
                <FileImage className="w-4 h-4 mr-2" />
                {t('GENERATEREVIEW_ACTIONS_LETTERHEAD')}
              </Button>
            )}
            {!hideDocumentActions && (
              <Button
                variant={isEditable ? 'outline' : 'default'}
                size={'sm'}
                onClick={handleDownloadButtonClick}
                disabled={isDownloading}
                aria-busy={isDownloading}
                className={
                  isEditable
                    ? 'border-border text-globe-grey'
                    : 'bg-selise-blue hover:bg-oxford-blue text-white hover:text-white shadow-md'
                }
              >
                {isDownloading ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {downloadLabel}
              </Button>
            )}
            {/* Save-as-Draft is org-only — guests don't have a drafts list. The
                icon animates between FilePenLine → spinner → check → idle. */}
            {
              !hideDocumentActions && isAuthenticated && canSaveDraft && (
                <motion.div
                  whileHover={saveDraftStatus === 'idle' ? { scale: 1.04 } : undefined}
                  whileTap={saveDraftStatus === 'idle' ? { scale: 0.96 } : undefined}
                  animate={
                    saveDraftStatus === 'saved'
                      ? { boxShadow: '0 0 0 3px rgba(0, 138, 241, 0.30)' }
                      : { boxShadow: '0 0 0 0 rgba(0, 138, 241, 0)' }
                  }
                  transition={{ duration: 0.2 }}
                  className="rounded-md inline-flex"
                >
                  <Button
                    disabled={saveDraftStatus === 'saving'}
                    variant="outline"
                    size={'sm'}
                    onClick={onSaveDraft}
                    className={
                      'w-40 justify-center border-border transition-colors ' +
                      (saveDraftStatus === 'saved'
                        ? 'text-selise-blue border-selise-blue/50'
                        : saveDraftStatus === 'error'
                          ? 'text-destructive border-destructive/40'
                          : 'text-globe-grey')
                    }
                    title={t('GENERATEREVIEW_ACTIONS_SAVEASDRAFT')}
                  >
                    <span className="relative inline-flex items-center justify-center size-4 mr-2">
                      <AnimatePresence mode="wait" initial={false}>
                        {saveDraftStatus === 'saving' && (
                          <motion.span
                            key="saving"
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.6 }}
                            transition={{ duration: 0.15 }}
                            className="absolute inset-0 inline-flex items-center justify-center"
                          >
                            <Loader className="size-4 animate-spin" />
                          </motion.span>
                        )}
                        {saveDraftStatus === 'saved' && (
                          <motion.span
                            key="saved"
                            initial={{ opacity: 0, scale: 0.4, rotate: -20 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            exit={{ opacity: 0, scale: 0.4 }}
                            transition={{ duration: 0.2, type: 'spring', stiffness: 400 }}
                            className="absolute inset-0 inline-flex items-center justify-center"
                          >
                            <Check className="size-4" />
                          </motion.span>
                        )}
                        {(saveDraftStatus === 'idle' || saveDraftStatus === 'error') && (
                          <motion.span
                            key="idle"
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.6 }}
                            transition={{ duration: 0.15 }}
                            className="absolute inset-0 inline-flex items-center justify-center"
                          >
                            <FilePenLine className="size-4" />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                    {saveDraftStatus === 'saving'
                      ? 'Saving…'
                      : saveDraftStatus === 'saved'
                        ? 'Saved'
                        : saveDraftStatus === 'error'
                          ? 'Retry'
                          : t('GENERATEREVIEW_ACTIONS_SAVEASDRAFT')}
                  </Button>
                </motion.div>
              )
            }
          </div >

          {/* Mobile actions: icon-only Send + 3-dot overflow menu. */}
          < div className="flex md:hidden items-center gap-2 shrink-0" >
            {isApprovalPage && isContractApproved && (
              <DocumentStatusBadge
                status="document_completed"
                label={t('GENERATEREVIEW_STATUS_APPROVED', { defaultValue: 'Approved' })}
              />
            )}
            {
              isEditable && signatureFields.length > 0 && (
                <Button
                  size={'sm'}
                  onClick={() => {
                    if (isApprovalPage || isContractApproved) {
                      handleSendToSignature();
                    } else {
                      setIsSendToSignatureModalOpen(true);
                    }
                  }}
                  variant="primary"
                  disabled={isPendingApproval}
                  aria-label={
                    isApprovalPage && !isContractApproved
                      ? t('GENERATEREVIEW_ACTIONS_APPROVECONTRACT', {
                        defaultValue: 'Approve Contract',
                      })
                      : t('GENERATEREVIEW_ACTIONS_SENDFORSIGNATURE')
                  }
                  title={
                    isPendingApproval
                      ? t('GENERATEREVIEW_PENDING_APPROVAL_SIGNATURE_DISABLED', {
                        defaultValue:
                          'The contract must be approved before it can be sent for signature.',
                      })
                      : undefined
                  }
                >
                  {isApprovalPage && !isContractApproved ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              )
            }
            {
              !hideDocumentActions && <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size={'sm'}
                    variant="outline"
                    aria-label={t('GENERATEREVIEW_ACTIONS_MORE', { defaultValue: 'More actions' })}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {isEditable && canLetterhead && (
                    <DropdownMenuItem onSelect={onLetterheadClick}>
                      <FileImage className="w-4 h-4 mr-2" />
                      {t('GENERATEREVIEW_ACTIONS_LETTERHEAD')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem disabled={isDownloading} onSelect={handleDownloadButtonClick}>
                    {isDownloading ? (
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {downloadLabel}
                  </DropdownMenuItem>
                  {isAuthenticated && canSaveDraft && (
                    <DropdownMenuItem
                      disabled={saveDraftStatus === 'saving'}
                      onSelect={(event) => {
                        event.preventDefault();
                        onSaveDraft();
                      }}
                    >
                      {saveDraftStatus === 'saving' ? (
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                      ) : saveDraftStatus === 'saved' ? (
                        <Check className="w-4 h-4 mr-2" />
                      ) : (
                        <FilePenLine className="w-4 h-4 mr-2" />
                      )}
                      {saveDraftStatus === 'saving'
                        ? 'Saving…'
                        : saveDraftStatus === 'saved'
                          ? 'Saved'
                          : saveDraftStatus === 'error'
                            ? 'Retry'
                            : t('GENERATEREVIEW_ACTIONS_SAVEASDRAFT')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            }
          </div >
        </div >
      </div >
      {!isApprovalPage && !isContractApproved && templateSlug && documentId && (
        <SendForReviewDialog
          open={isSendToSignatureModalOpen}
          onOpenChange={setIsSendToSignatureModalOpen}
          onConfirm={handleSendToSignature}
          templateSlug={templateSlug}
          documentId={documentId}
        />
      )}
    </header >
  );
};

export default TemplatePDFReviewHeader;
